import { menuItems, settingsItems } from "./constants";
import { translations } from "./Translation/staticTranslation";
import { apiAccessKeyToUiAccess } from "../Apis/admin-roles";

export const CREATE_ROLES_PAGE_SIZE = 10;

/** Ant Design table pagination aligned with AI Users list (`UserManagmentTable`). */
export function buildCreateRolesTablePagination({
  current,
  total,
  pageSize = CREATE_ROLES_PAGE_SIZE,
  onChange,
}) {
  return {
    current,
    total,
    pageSize,
    showSizeChanger: false,
    hideOnSinglePage: true,
    position: ["bottomRight"],
    onChange: (page) => onChange(page),
  };
}

/**
 * Sidebar + settings paths for fallback permission labels when API catalog is empty.
 */
/** Prefer sidebar menu label over API catalog label (e.g. Plans → Plan and Pricing). */
export function getModuleDisplayLabel(accessKey, apiLabel, lang) {
  const sidebarT = translations[lang]?.Sidebar || {};
  const fromMenu = menuItems.find((m) => m.path === accessKey);
  if (fromMenu) {
    return sidebarT[fromMenu.name] || fromMenu.name;
  }
  const fromSettings = settingsItems.find(
    (s) => s.path.replace(/^\//, "") === accessKey,
  );
  if (fromSettings) {
    return sidebarT[fromSettings.name] || fromSettings.name;
  }
  return apiLabel || accessKey;
}

export function getPermissionModuleRows() {
  const fromMenu = menuItems
    .filter((m) => m.name !== "Admin roles")
    .map((m) => ({ id: m.path, nameKey: m.name }));
  const fromSettings = settingsItems.map((s) => ({
    id: s.path.replace(/^\//, ""),
    nameKey: s.name,
  }));
  return [...fromMenu, ...fromSettings];
}

/**
 * Flattens Django REST / nested validation payloads, e.g.
 * `{ role_name: ["Role with this name already exists."] }`
 */
function collectErrorStrings(value, depth = 0) {
  if (depth > 12 || value == null) return [];
  if (typeof value === "string") return value.trim() ? [value.trim()] : [];
  if (Array.isArray(value)) return value.flatMap((v) => collectErrorStrings(v, depth + 1));
  if (typeof value === "object") {
    return Object.values(value).flatMap((v) => collectErrorStrings(v, depth + 1));
  }
  return [];
}

/** Axios-style error → user-facing string (same pattern as other pages). */
export function getCreateRolesApiErrorMessage(error, fallback) {
  const err = error?.response?.data;
  if (typeof err === "string" && err.trim()) return err.trim();

  if (err && typeof err === "object") {
    const single =
      typeof err.message === "string"
        ? err.message.trim()
        : typeof err.detail === "string"
          ? err.detail.trim()
          : typeof err.error === "string"
            ? err.error.trim()
            : "";
    if (single) return single;

    const nested = collectErrorStrings(err);
    if (nested.length) return nested.join(" ");
  }

  // Prefer fallback over Axios's generic "Request failed with status code 400"
  if (!error?.response && typeof error?.message === "string" && error.message.trim()) {
    return error.message.trim();
  }

  return fallback;
}

const ACCESS_LEVEL_FALLBACK_LABEL_KEY = {
  full: "access_full",
  edit: "access_edit",
  partial: "access_partial",
};

export function accessLabelForUiLevel(uiLevel, accessTypesCatalog, t) {
  const fallbackKey = ACCESS_LEVEL_FALLBACK_LABEL_KEY[uiLevel];
  if (!fallbackKey) return "";
  const row = accessTypesCatalog?.find(
    (a) => apiAccessKeyToUiAccess(a.key) === uiLevel,
  );
  return row?.label ?? t[fallbackKey];
}

export function buildPermissionsSummary(
  accessByModule,
  lang,
  t,
  moduleCatalog,
  accessTypesCatalog,
) {
  const sidebarT = translations[lang]?.Sidebar || {};
  const fallbackRows = getPermissionModuleRows();
  const parts = [];
  for (const [accessKey, level] of Object.entries(accessByModule || {})) {
    if (!ACCESS_LEVEL_FALLBACK_LABEL_KEY[level]) continue;
    const catalogLabel = moduleCatalog?.find(
      (r) => r.accessKey === accessKey,
    )?.label;
    let label = getModuleDisplayLabel(accessKey, catalogLabel, lang);
    if (!catalogLabel && !label) {
      const fr = fallbackRows.find((r) => r.id === accessKey);
      label = fr ? sidebarT[fr.nameKey] || fr.nameKey : accessKey;
    }
    const accessLabel = accessLabelForUiLevel(level, accessTypesCatalog, t);
    parts.push(`${label} (${accessLabel})`);
  }
  return parts.length ? parts.join(", ") : t.access_none;
}

/** Users tab: human-readable role label (API name or catalog lookup). */
export function userRoleColumnDisplay(record, roleNameByKey, lang = "ar") {
  const fromApi = record?.roleNameFromApi;
  if (fromApi != null && String(fromApi).trim() !== "") {
    const raw = String(fromApi).trim();
    const labels = translations[lang]?.createRolesPage?.api_role_labels;
    if (labels && typeof labels === "object") {
      const upper = raw.toUpperCase();
      const mapped = labels[raw] ?? labels[upper];
      if (mapped != null && String(mapped).trim() !== "") {
        return String(mapped).trim();
      }
    }
    return raw;
  }
  const k =
    record?.marketRoleKey != null ? String(record.marketRoleKey).trim() : "";
  if (k && roleNameByKey?.size) {
    const v = roleNameByKey.get(k);
    if (v != null && String(v).trim() !== "") return String(v).trim();
  }
  return k || "—";
}

export function isValidCreateUserEmail(value) {
  const s = String(value || "").trim();
  if (!s) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
}

/** Row is a pending superadmin invite (no `user_id` yet) — cancel with `invite_id`. */
export function isPendingSuperadminInvite(record) {
  if (!record || typeof record !== "object") return false;
  const st =
    record.status != null ? String(record.status).trim().toLowerCase() : "";
  if (st !== "pending") return false;
  const inv = record.inviteId;
  return inv != null && Number.isFinite(Number(inv));
}

/**
 * Maps a user's `marketRoleKey` (API `role` code or id string) to the role row `id`
 * used by the Create User role Select (`value` matches superadmin role primary key).
 * @param {Array<{ id?: string, roleCode?: string }>} roles from `mapSuperAdminRoleToRow`
 * @param {string|undefined|null} marketRoleKey
 * @returns {string|undefined}
 */
export function findRoleSelectValueForMarketKey(roles, marketRoleKey) {
  const key =
    marketRoleKey != null ? String(marketRoleKey).trim() : "";
  if (!key || !Array.isArray(roles) || !roles.length) return undefined;
  const match = roles.find(
    (r) =>
      String(r.roleCode ?? "").trim() === key ||
      String(r.id ?? "").trim() === key,
  );
  return match?.id != null ? String(match.id) : undefined;
}
